import { useState } from 'react'
import { useAuth } from '../../context/AuthContext'
import { useToast } from '../../context/ToastContext'
import { Permission } from '../../types/auth.types'
import { supabase } from '../../lib/supabase'
import {
  Home, Users, CalendarDays, Clock, Briefcase, Megaphone,
  GitBranch, FileText, Receipt, BarChart2, IndianRupee,
  Zap, LogOut, Menu, X, Bell, ChevronRight, KeyRound, Eye, EyeOff,
} from 'lucide-react'

export type FounderSection =
  | 'home' | 'team' | 'tasks'
  | 'pipeline' | 'invoices' | 'expenses' | 'clients'
  | 'campaigns' | 'ad-performance' | 'budget'
  | 'content-calendar' | 'scheduling' | 'projects'

interface NavItem {
  id: FounderSection
  label: string
  icon: React.ElementType
  requiredPermission?: Permission
  badge?: string
}

const ALL_NAV: NavItem[] = [
  { id: 'home',             label: 'Home',             icon: Home },
  { id: 'team',             label: 'My Team',          icon: Users },
  { id: 'tasks',            label: 'Tasks',            icon: Clock },
  // Sales & Ops
  { id: 'pipeline',         label: 'Pipeline',         icon: GitBranch,   requiredPermission: Permission.VIEW_PIPELINE },
  { id: 'clients',          label: 'Clients',          icon: Briefcase,   requiredPermission: Permission.VIEW_CLIENTS },
  { id: 'invoices',         label: 'Invoices',         icon: FileText,    requiredPermission: Permission.VIEW_INVOICES },
  { id: 'expenses',         label: 'Expenses',         icon: Receipt,     requiredPermission: Permission.VIEW_EXPENSES },
  // Paid Ads
  { id: 'campaigns',        label: 'Campaigns',        icon: Megaphone,   requiredPermission: Permission.VIEW_CAMPAIGNS },
  { id: 'ad-performance',   label: 'Ad Performance',   icon: BarChart2,   requiredPermission: Permission.VIEW_AD_PERFORMANCE },
  { id: 'budget',           label: 'Budget Tracker',   icon: IndianRupee, requiredPermission: Permission.VIEW_BUDGET_TRACKER },
  // Production
  { id: 'content-calendar', label: 'Content Calendar', icon: CalendarDays, requiredPermission: Permission.VIEW_CONTENT_CALENDAR },
  { id: 'scheduling',       label: 'Scheduling',       icon: Clock,       requiredPermission: Permission.VIEW_SCHEDULING },
  { id: 'projects',         label: 'Projects',         icon: Briefcase,   requiredPermission: Permission.VIEW_PROJECTS },
]

const FOUNDER_COLOR: Record<string, string> = {
  bunny: '#ffc600', hj: '#e8a000', darshu: '#ff8c00',
}
const FOUNDER_LABEL: Record<string, string> = {
  bunny: 'Production', hj: 'Sales & Ops', darshu: 'Paid Ads',
}

interface Props {
  active: FounderSection
  onNav: (s: FounderSection) => void
  children: React.ReactNode
}

export default function FounderLayout({ active, onNav, children }: Props) {
  const { currentUser, logout, hasPermission } = useAuth()
  const { toast } = useToast()
  const [mobileOpen, setMobileOpen]   = useState(false)
  const [showPwModal, setShowPwModal] = useState(false)
  const [pwForm, setPwForm]   = useState({ newPw: '', confirm: '' })
  const [pwError, setPwError] = useState('')
  const [pwLoading, setPwLoading] = useState(false)
  const [showPw, setShowPw]   = useState(false)

  const handleChangePw = async (e: React.FormEvent) => {
    e.preventDefault(); setPwError('')
    if (pwForm.newPw.length < 8)          { setPwError('Minimum 8 characters.'); return }
    if (pwForm.newPw !== pwForm.confirm)  { setPwError('Passwords do not match.'); return }
    setPwLoading(true)
    const { error } = await supabase.auth.updateUser({ password: pwForm.newPw })
    setPwLoading(false)
    if (error) { setPwError(error.message); return }
    toast('Password updated!'); setShowPwModal(false); setPwForm({ newPw: '', confirm: '' })
  }

  const fKey   = currentUser?.founderKey ?? 'hj'
  const fColor = FOUNDER_COLOR[fKey] ?? '#ffc600'
  const fLabel = FOUNDER_LABEL[fKey] ?? 'Founder'

  // Filter nav — hidden items are completely invisible (no greyed-out)
  const visibleNav = ALL_NAV.filter(item =>
    !item.requiredPermission || hasPermission(item.requiredPermission)
  )

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="flex items-center gap-2.5 px-5 py-5 border-b" style={{ borderColor: 'rgba(255,198,0,0.12)' }}>
        <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: fColor }}>
          <Zap size={15} color="#000" fill="#000" />
        </div>
        <div>
          <p className="text-sm font-bold text-white" style={{ fontFamily: 'Syne, sans-serif' }}>Clickbait</p>
          <p className="text-xs font-medium" style={{ color: fColor, fontSize: '10px', letterSpacing: '0.08em' }}>
            {fLabel.toUpperCase()}
          </p>
        </div>
      </div>

      {/* Nav — only permitted items visible */}
      <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
        {visibleNav.map(({ id, label, icon: Icon, badge }) => {
          const isActive = active === id
          return (
            <button
              key={id}
              onClick={() => { onNav(id); setMobileOpen(false) }}
              className="w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium text-left transition-all"
              style={isActive ? {
                borderLeft: `2px solid ${fColor}`,
                color: fColor,
                background: `${fColor}12`,
                borderRadius: '0 8px 8px 0',
              } : {
                borderLeft: '2px solid transparent',
                color: '#a0a0a0',
                borderRadius: '0 8px 8px 0',
              }}
            >
              <Icon size={16} />
              <span className="flex-1">{label}</span>
              {badge && (
                <span className="text-xs px-1.5 py-0.5 rounded font-semibold"
                  style={{ background: `${fColor}18`, color: fColor, fontSize: '10px' }}>
                  {badge}
                </span>
              )}
              {isActive && <ChevronRight size={13} className="flex-shrink-0" />}
            </button>
          )
        })}
      </nav>

      {/* User */}
      <div className="px-4 py-4 border-t" style={{ borderColor: 'rgba(255,198,0,0.12)' }}>
        <div className="flex items-center gap-3 mb-3">
          <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
            style={{ background: fColor, color: '#000' }}>
            {currentUser?.avatarInitials}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-white truncate">{currentUser?.name}</p>
            <p className="text-xs truncate" style={{ color: '#555' }}>{fLabel}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowPwModal(true)}
            className="flex-1 flex items-center justify-center gap-1.5 px-2 py-2 rounded-lg text-xs transition-all"
            style={{ color: '#a0a0a0', border: '1px solid rgba(255,198,0,0.12)' }}
            onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,198,0,0.05)')}
            onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
            <KeyRound size={12} /> Password
          </button>
          <button onClick={logout}
            className="flex-1 flex items-center justify-center gap-1.5 px-2 py-2 rounded-lg text-xs transition-all"
            style={{ color: '#cc3300', border: '1px solid rgba(204,51,0,0.2)' }}
            onMouseEnter={e => (e.currentTarget.style.background = 'rgba(204,51,0,0.08)')}
            onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
            <LogOut size={12} /> Sign out
          </button>
        </div>
      </div>
    </div>
  )

  return (
    <div className="flex h-screen overflow-hidden" style={{ background: '#000' }}>
      {/* Desktop sidebar */}
      <aside className="hidden md:flex flex-col w-56 flex-shrink-0"
        style={{ background: '#0f0f0f', borderRight: '1px solid rgba(255,198,0,0.12)' }}>
        <SidebarContent />
      </aside>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="absolute inset-0 bg-black/70" onClick={() => setMobileOpen(false)} />
          <aside className="absolute left-0 top-0 h-full w-56 z-50"
            style={{ background: '#0f0f0f', borderRight: '1px solid rgba(255,198,0,0.12)' }}>
            <SidebarContent />
          </aside>
        </div>
      )}

      {/* Main */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Mobile header */}
        <div className="md:hidden flex items-center justify-between px-4 py-3"
          style={{ background: '#0f0f0f', borderBottom: '1px solid rgba(255,198,0,0.12)' }}>
          <span className="font-bold text-white text-sm" style={{ fontFamily: 'Syne, sans-serif' }}>
            {currentUser?.name}
          </span>
          <div className="flex items-center gap-3">
            <Bell size={18} style={{ color: '#a0a0a0' }} />
            <button onClick={() => setMobileOpen(v => !v)} style={{ color: '#fff' }}>
              {mobileOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
        <main className="flex-1 overflow-y-auto">{children}</main>
      </div>

      {/* Change Password Modal */}
      {showPwModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.85)' }}>
          <div className="w-full max-w-sm rounded-2xl p-6" style={{ background: '#0f0f0f', border: '1px solid rgba(255,198,0,0.35)' }}>
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-2">
                <KeyRound size={16} style={{ color: '#ffc600' }} />
                <h2 className="text-base font-bold text-white" style={{ fontFamily: 'Syne, sans-serif' }}>Change Password</h2>
              </div>
              <button onClick={() => { setShowPwModal(false); setPwError('') }} style={{ color: '#555' }}><X size={18} /></button>
            </div>
            <form onSubmit={handleChangePw} className="space-y-3">
              <div>
                <label className="text-xs font-semibold block mb-1" style={{ color: '#a0a0a0' }}>NEW PASSWORD</label>
                <div className="relative">
                  <input type={showPw ? 'text' : 'password'} value={pwForm.newPw}
                    onChange={e => setPwForm(f => ({ ...f, newPw: e.target.value }))}
                    style={{ width: '100%', background: '#1a1a1a', border: '1px solid rgba(255,198,0,0.2)', borderRadius: 8, padding: '9px 36px 9px 12px', color: '#fff', fontSize: 13, outline: 'none' }}
                    placeholder="Minimum 8 characters" />
                  <button type="button" onClick={() => setShowPw(v => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2" style={{ color: '#555' }}>
                    {showPw ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold block mb-1" style={{ color: '#a0a0a0' }}>CONFIRM PASSWORD</label>
                <input type="password" value={pwForm.confirm}
                  onChange={e => setPwForm(f => ({ ...f, confirm: e.target.value }))}
                  style={{ width: '100%', background: '#1a1a1a', border: '1px solid rgba(255,198,0,0.2)', borderRadius: 8, padding: '9px 12px', color: '#fff', fontSize: 13, outline: 'none' }}
                  placeholder="Re-enter new password" />
              </div>
              {pwError && <p className="text-xs" style={{ color: '#cc3300' }}>{pwError}</p>}
              <div className="flex gap-3 pt-1">
                <button type="button" onClick={() => { setShowPwModal(false); setPwError('') }}
                  className="flex-1 py-2.5 rounded-lg text-sm font-semibold"
                  style={{ border: '1px solid rgba(255,198,0,0.2)', color: '#a0a0a0', background: 'transparent' }}>Cancel</button>
                <button type="submit" disabled={pwLoading}
                  className="flex-1 py-2.5 rounded-lg text-sm font-semibold flex items-center justify-center gap-2"
                  style={{ background: pwLoading ? 'rgba(255,198,0,0.5)' : fColor, color: '#000' }}>
                  {pwLoading && <span className="w-3.5 h-3.5 border-2 border-black/30 border-t-black rounded-full animate-spin" />}
                  Update
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
